import axios from 'axios';

import { accountServices } from '../services/accountServices';
import { scenarioActions } from './scenarioActions';
import { loadCurrenciesForScenario } from './currencyActions';
import { reportingActions } from './reportingActions';
import { accountConstants, scenarioConstants } from '../constants';
import { financialGroupTypeAndName } from '../utils/financialConstants';
import { pushUndo } from '../utils/undoHistory';
import {
	fetchAccountById,
	fetchAccountByAssociatedId,
	fetchFinancialGroupByAccount,
	fetchParentAccountGroupByAccount,
	findAllAccounts,
} from '../../utils/accountStructure/accountStructureUtils';


export const accountActions = {
	getAccountStructure,
	createAccount,
	createSubAccount,
	insertAccount,
	updateAccount,
	updateFormulas,
	deleteAccount,
	moveAccountUp,
	moveAccountDown,
	moveAccountLeft,
	moveAccountRight,
	createCustomTab,
	updateCustomTab,
	deleteCustomTab,
	createCustomTabAccount,
	updateCustomTabAccountSettings,
	updateCustomTabAccount,
	moveCustomTabAccountUp,
	moveCustomTabAccountDown,
	deleteCustomTabAccount,
	createChangeInAccount,
	createSeparator,
	updateSeparator,
    deleteSeparator,
	moveSeparator,
	reorderCustomTabs,
};

export function getAccountStructure( options = {}) {
    return async (dispatch, getState) => {
         const force = options.force === true;

        if (
            !force &&
            getState().accounts &&
            getState().accounts.accountStructure
        ) {
            return;
        }

        dispatch(request());

		try {
			const response = await axios.get('/accounts');

            if (response.status === 200) {
                dispatch(success(response.data));
                
                return response.data;
            }
            
            if (response.status === 500) dispatch(failure(response.data.toString()));
        
        } catch (error) {
            console.error(error);
            if (error.response) {
                dispatch(failure(error.response.data.toString()));
            } else {
                dispatch(failure('Unable to update account structure at this moment. Please try again later.'));
            }
        }
    };

	function request() {
		return { type: accountConstants.GET_ACCOUNT_STRUCTURE_REQUEST };
	}
	function success(accountStructure) {
		return {
			type: accountConstants.GET_ACCOUNT_STRUCTURE_SUCCESS,
			accountStructure,
		};
	}
	function failure(error) {
		return { type: accountConstants.GET_ACCOUNT_STRUCTURE_FAILURE, error };
	}
}

function createAccount(ftype, accountStructure) {
	return async (dispatch, getState) => {
		dispatch(request());

		try {
			const periods = getState().periods.periods;
			const newAccountNumber = accountServices.findNewAccountNumber(accountStructure);

			const response = await axios.post('/accounts', {
				ftype: ftype,
				account_number: newAccountNumber,
				index: accountServices.findFinancialAccounts(ftype, accountStructure).length,
				name: 'New account',
				group_id: financialGroupTypeAndName[ftype][1],
				financial_group_type: financialGroupTypeAndName[ftype][0],
				periods: periods,
			});

			if (response.status === 201) {
				const newAccount = response.data.newAccount;
				const newValues = response.data.newValues;
				const newAccountStructure = response.data.accountStructure;

				await dispatch(scenarioActions.createValues(newAccount, newValues));
				dispatch(success(newAccountStructure));
				
				// Refresh scenario + currencies for the currently viewed scenario
				const sid = getState().organization.currentScenarioId;
				if (sid && sid !== -1) {
					await dispatch(scenarioActions.getScenario(sid));
					await dispatch(loadCurrenciesForScenario(sid));
				}
			}
			
			if (response.status === 500) dispatch(failure(response.data.toString()));
		
		} catch (error) {
			
			console.error(error);
			
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure(
						'Unable to update account structure at this moment. Please try again later.',
					),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.CREATE_ACCOUNT_REQUEST };
	}
	function success(newAccountStructure) {
		return {
			type: accountConstants.CREATE_ACCOUNT_SUCCESS,
			newAccountStructure,
		};
	}
	function failure(error) {
		return { type: accountConstants.CREATE_ACCOUNT_FAILURE, error };
	}
}

function createSubAccount(account, ftype, dataIndex) {
  return async (dispatch, getState) => {
    // prevent concurrent calls
    if (getState().accounts.creatingSubAccount) return;

    dispatch(request());

    try {
      if (!account?.account_id) {
        dispatch(failure("No parent account selected."));
        return;
      }

      const periods = getState().periods.periods;
      const accountStructure = getState().accounts.accountStructure; // <-- fresh
      if (!accountStructure) {
        dispatch(failure("Account structure not loaded."));
        return;
      }

      const newAccountNumber = accountServices.findNewAccountNumber(accountStructure);
      const newFinancialAccounts = [
        ...accountServices.findFinancialAccounts(ftype, accountStructure),
      ];

      const parentAccount = {};
      accountServices.findAccount(parentAccount, account.account_id, newFinancialAccounts);

      if (!parentAccount.copy) {
        dispatch(failure("Parent account not found."));
        return;
      }

      const response = await axios.post("/accounts/sub-account", {
        account_number: newAccountNumber,
        parent: account.account_id,
        index: parentAccount.copy.children ? parentAccount.copy.children.length : 0,
        name: "New account",
        group_id: financialGroupTypeAndName[ftype][1],
        financial_group_type: financialGroupTypeAndName[ftype][0],
        dataIndex,
        periods,
      });

      if (response.status === 201) {
        const newSubAccount = response.data.newSubAccount;
        const newSubValues = response.data.newSubValues;
        let newAccountStructure = response.data.accountStructure;

        const parentAutoAddsChildren = accountStructure.custom.some((tab) =>
          accountServices.isPinnedWithAutoAddChildren(
            newSubAccount.parent,
            tab.custom_tab_accounts
          )
        );

        if (parentAutoAddsChildren) {
          const subAccountsResponse = await axios.post("/custom-tab/sub-account", {
            account: newSubAccount,
          });

          if (subAccountsResponse.status === 201) {
            const customPatched = subAccountsResponse.data;
            newAccountStructure = {
              ...newAccountStructure,
              ...customPatched,
              currencies: Array.isArray(customPatched.currencies)
                ? customPatched.currencies
                : newAccountStructure.currencies,
              baseCurrencyId:
                customPatched.baseCurrencyId ?? newAccountStructure.baseCurrencyId,
              scenarioCurrencyId:
                customPatched.scenarioCurrencyId ?? newAccountStructure.scenarioCurrencyId,
            };
          }
        }

        await dispatch(scenarioActions.createValues(newSubAccount, newSubValues));
        dispatch(success(newAccountStructure));

        const sid = getState().organization.currentScenarioId;
        if (sid && sid !== -1) {
          await dispatch(scenarioActions.getScenario(sid));
          await dispatch(loadCurrenciesForScenario(sid));
        }
      }
    } catch (error) {
      console.error(error);
      if (error.response) dispatch(failure(String(error.response.data)));
      else dispatch(failure("Unable to update account structure at this moment. Please try again later."));
    }
  };

  function request() {
    return { type: accountConstants.CREATE_SUB_ACCOUNT_REQUEST };
  }
  function success(accountStructure) {
    return { type: accountConstants.CREATE_SUB_ACCOUNT_SUCCESS, payload: { accountStructure } };
  }
  function failure(error) {
    return { type: accountConstants.CREATE_SUB_ACCOUNT_FAILURE, error };
  }
}


function updateAccount(oldAccount, account) {
	return async (dispatch, getState) => {
		dispatch(request());

		try {
			const currentScenario = getState().scenarios.currentScenario;

			const response = await axios.put('/accounts/' + account.account_id, {
				account,
				currentScenarioId: currentScenario != null ? currentScenario.scenario_id : -1,
			});

			if (response.status === 200) {
				if (currentScenario != null && response.data.parsedScenario) {
				dispatch(currentScenarioSuccess(response.data.parsedScenario));
				}

				dispatch(success(response.data));
			}

			if (response.status === 500) dispatch(failure(response.data.toString()));
			} catch (error) {
			console.error(error);
			if (error.response) dispatch(failure(error.response.data.toString()));
			else dispatch(failure('Unable to update account at this moment. Please try again later.'));
			}
		};

	function currentScenarioSuccess(parsedScenario) {
		return { type: scenarioConstants.GET_SCENARIO_SUCCESS, parsedScenario };
	}

	function request() {
		return { type: accountConstants.UPDATE_ACCOUNTS_REQUEST };
	}
	function success(payload) {
		return {
			type: accountConstants.UPDATE_ACCOUNTS_SUCCESS,
			payload,
		};
	}
	function failure(error) {
		return { type: accountConstants.UPDATE_ACCOUNTS_FAILURE, error };
	}
}

function updateFormulas(account, period, formula, isCustomAccount, addFormulaAllPeriods) {
  return async (dispatch) => {
    dispatch(request());

    try {
      const isActuallyCustomAccount =
        account.custom_tab_accounts_id != null && account.account != null;

      const realAccount = isActuallyCustomAccount ? account.account : account;

      if (!realAccount?.formulas) {
        throw new Error('Missing formulas on account');
      }

      const response = await axios.put('/update-formulas', {
        account,
        period,
        formula,
        formulas: realAccount.formulas,
        isCustomAccount: isActuallyCustomAccount,
        addFormulaAllPeriods,
      });

      if (response.status === 200) {
        dispatch(success({
          account,
          realAccount,
          isCustomAccount: isActuallyCustomAccount,
          patch: response.data,
        }));

        const periodLabel = addFormulaAllPeriods ? `${period.slice(6)} onwards` : period.slice(6);
        dispatch(pushUndo(`Formula ${realAccount.account_number} · ${periodLabel}`, response.data?.undo));
      }

      if (response.status === 500) dispatch(failure(response.data.toString()));
    } catch (error) {
      console.error(error);
      if (error.response) dispatch(failure(error.response.data.toString()));
      else dispatch(failure('Unable to update formulas at this moment. Please try again later.'));
    }
  };

  function request() {
    return { type: accountConstants.UPDATE_FORMULAS_REQUEST };
  }

  function success(payload) {
    return {
      type: accountConstants.UPDATE_FORMULAS_SUCCESS,
      payload,
    };
  }

  function failure(error) {
    return { type: accountConstants.UPDATE_FORMULAS_FAILURE, error };
  }
}

function deleteAccount(account) {
	return async (dispatch, getState) => {
		dispatch(request());

		try {
			const accountStructure = getState().accounts.accountStructure;

			let associatedAccount = null;
			let changeInAccount = null;
			let accountGroup = null;
			let secondAccountGroup = null; // only if change in account or associated account

			if (account.associated_account_id == null && !account.is_change_in) {
				accountGroup =
					account.parent == null
						? fetchFinancialGroupByAccount(account, accountStructure)
						: fetchParentAccountGroupByAccount(account, accountStructure);
			} else if (account.associated_account_id == null && account.is_change_in) {
				const allAccounts = [];
				findAllAccounts(allAccounts, accountStructure);

				allAccounts.forEach((potentialAssociatedAccount) => {
					if (
						potentialAssociatedAccount.associated_account_id != null &&
						account.account_id === potentialAssociatedAccount.account_id
					) {
						associatedAccount = potentialAssociatedAccount;
					}
				});

				accountGroup =
					account.parent == null
						? fetchFinancialGroupByAccount(account, accountStructure)
						: fetchParentAccountGroupByAccount(account, accountStructure);

				secondAccountGroup =
					associatedAccount.parent == null
						? fetchFinancialGroupByAccount(associatedAccount, accountStructure)
						: fetchParentAccountGroupByAccount(associatedAccount, accountStructure);
			} else if (account.associated_account_id != null) {
				const allAccounts = [];
				findAllAccounts(allAccounts, accountStructure);

				allAccounts.forEach((potentialChangeInAccount) => {
					if (
						potentialChangeInAccount.associated_account_id == null &&
						account.account_id === potentialChangeInAccount.account_id
					) {
						changeInAccount = potentialChangeInAccount;
					}
				});

				accountGroup =
					account.parent == null
						? fetchFinancialGroupByAccount(account, accountStructure)
						: fetchParentAccountGroupByAccount(account, accountStructure);

				secondAccountGroup =
					changeInAccount.parent == null
						? fetchFinancialGroupByAccount(changeInAccount, accountStructure)
						: fetchParentAccountGroupByAccount(changeInAccount, accountStructure);
			}

			const response = await axios.delete('/accounts/', {
				data: {
					account: account,
					associatedAccount: associatedAccount,
					changeInAccount: changeInAccount,
					accountGroup: accountGroup,
					secondAccountGroup: secondAccountGroup,
				},
			});

			if (response.status === 200) {
				dispatch(success(response.data));
				dispatch(reportingActions.setReportingIntegrityFalse());
			}
			if (response.status === 500) dispatch(failure(response.data.toString()));
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure(
						'Unable to update account structure at this moment. Please try again later.',
					),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.DELETE_ACCOUNT_REQUEST, };
	}
	function success(accountStructure) {
		return {
			type: accountConstants.DELETE_ACCOUNT_SUCCESS,
			accountStructure,
		};
	}
	function failure(error) {
		return { type: accountConstants.DELETE_ACCOUNT_FAILURE, error };
	}
}

function insertAccount(account) {
	return async (dispatch, getState) => {
		dispatch(request());

		try {
			const accountStructure = getState().accounts.accountStructure;
			const periods = getState().periods.periods;
			const scenarioIds = Array.from(getState().scenarios.scenarios.keys());

			const accountGroup =
				account.parent == null
					? fetchFinancialGroupByAccount(account, accountStructure)
					: fetchParentAccountGroupByAccount(account, accountStructure);

			const accountNumber = accountServices.findNewAccountNumber(accountStructure);

			const response = await axios.post('/accounts/insert', {
				account: account,
				accountGroup: accountGroup,
				accountNumber: accountNumber,
				periods: periods,
				scenarioIds: scenarioIds,
			});

			if (response.status === 201) {
				const newAccount = response.data.newAccount;
				const newValues = response.data.newValues;

				dispatch(scenarioActions.createValues(newAccount, newValues));
				dispatch(success(response.data.newAccountStructure));
			}

			if (response.status === 500) {
				dispatch(failure(response.data.toString()));
			}
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure('Unable to insert account up at this moment. Please try again later.'),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.INSERT_ACCOUNT_REQUEST };
	}
	function success(accountStructure) {
		return {
			type: accountConstants.INSERT_ACCOUNT_SUCCES,
			accountStructure,
		};
	}
	function failure(error) {
		return { type: accountConstants.INSERT_ACCOUNT_FAILURE, error };
	}
}

const getTreeId = (item) =>
    item?.associated_account_id != null
        ? `associated:${item.associated_account_id}`
        : `account:${item?.account_id}`;

const getCurrentIndex = (account, accountGroup) =>
    accountGroup.findIndex(
        (groupAccount) =>
            getTreeId(groupAccount) === getTreeId(account),
    );

function moveAccountUp(account) {
	return async (dispatch, getState) => {
	
		dispatch(request());

		try {
			const accountStructure = getState().accounts.accountStructure;

			const accountGroup =
				account.parent == null
					? fetchFinancialGroupByAccount(account, accountStructure)
					: fetchParentAccountGroupByAccount(account, accountStructure);

			if (!Array.isArray(accountGroup)) {
					dispatch(
						failure(
							'Unable to find the current account group.',
						),
					);
					return;
				}

				const currentIndex =  getCurrentIndex(account, accountGroup);

				// Top account cannot be moved further up
				if (currentIndex <= 0) {
					dispatch(
						failure(
							currentIndex < 0
								? 'Unable to find the account in its current group.'
								: 'Account cannot be moved further up.',
						),
					);
					return;
				}
				const response = await axios.put('/accounts/move/up', {
					moveUpAccount: { ...account, index: currentIndex },
					accountGroup: accountGroup,
				});

				if (response.status === 200) dispatch(success(response.data));
				if (response.status === 500) dispatch(failure(response.data.toString()));
			
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure('Unable to move account up at this moment. Please try again later.'),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.MOVE_ACCOUNT_UP_REQUEST };
	}
	function success(payload) {
		return {
			type: accountConstants.MOVE_ACCOUNT_UP_SUCCESS,
			payload,
		};
	}
	function failure(error) {
		return { type: accountConstants.MOVE_ACCOUNT_UP_FAILURE, error };
	}
}

function moveAccountDown(account) {
	return async (dispatch, getState) => {
		dispatch(request());

		try {
			const accountStructure = getState().accounts.accountStructure;

			const accountGroup =
				account.parent == null
					? fetchFinancialGroupByAccount(account, accountStructure)
					: fetchParentAccountGroupByAccount(account, accountStructure);

					if (!Array.isArray(accountGroup)) {
				dispatch(failure('Unable to find the current account group.'));
				return;
			}

			const getTreeId = (item) =>
				item?.associated_account_id != null
					? `associated:${item.associated_account_id}`
					: `account:${item?.account_id}`;

			const currentIndex =  getCurrentIndex(account, accountGroup);

			// bottom accounts cannot be moved further down
			if (
				currentIndex >= 0 &&
				currentIndex < accountGroup.length - 1
			) {
				const response = await axios.put('/accounts/move/down', {
					 moveDownAccount: {
						...account,
						index: currentIndex,
					},
					accountGroup,
				});

				if (response.status === 200) dispatch(success(response.data));
				if (response.status === 500) dispatch(failure(response.data.toString()));
			}
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure('Unable to move account down at this moment. Please try again later.'),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.MOVE_ACCOUNT_DOWN_REQUEST };
	}
	function success(payload) {
		return {
			type: accountConstants.MOVE_ACCOUNT_DOWN_SUCCESS,
			payload,
		};
	}
	function failure(error) {
		return { type: accountConstants.MOVE_ACCOUNT_DOWN_FAILURE, error };
	}
}

function moveAccountLeft(account) {
	return async (dispatch, getState) => {
		dispatch(request());

		try {
			const accountStructure = getState().accounts.accountStructure;

			const isAssociatedAccount = account.associated_account_id != null;

			if (account.parent != null && (isAssociatedAccount || !account.is_change_in)) {
				const financialGroup = fetchFinancialGroupByAccount(account, accountStructure);

				let parent = {};
				fetchAccountById(account.parent, parent, financialGroup);
				parent = parent['account'];

				// in case of associated account
				if (parent == null) {
					parent = {};
					fetchAccountByAssociatedId(account.parent, parent, financialGroup);
					parent = parent['account'];
				}

				const oldAccountGroup = fetchParentAccountGroupByAccount(account, accountStructure);

				const destinationAccountGroup =
					parent.parent == null
						? fetchFinancialGroupByAccount(parent, accountStructure)
						: fetchParentAccountGroupByAccount(parent, accountStructure);

				const destinationParentId = parent.parent;
				const destinationIndex = parent.index;

				const response = await axios.put('/accounts/move/left', {
					account,
					oldAccountGroup,
					destinationAccountGroup,
					destinationParentId,
					destinationIndex,
				});

				if (response.status === 200) dispatch(success(response.data));
				if (response.status === 500) dispatch(failure(response.data.toString()));
			} else {
				dispatch(failure('Account cannot be moved left.'));
			}
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure(
						'Unable to move account to the right at this moment. Please try again later.',
					),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.MOVE_ACCOUNT_LEFT_REQUEST };
	}
	function success(accountStructure) {
		return {
			type: accountConstants.MOVE_ACCOUNT_LEFT_SUCCESS,
			accountStructure,
		};
	}
	function failure(error) {
		return { type: accountConstants.MOVE_ACCOUNT_LEFT_FAILURE, error };
	}
}

function moveAccountRight(account) {
	return async (dispatch, getState) => {
		dispatch(request());

		try {
			const accountStructure = getState().accounts.accountStructure;

			const accountGroup =
				account.parent == null
					? fetchFinancialGroupByAccount(account, accountStructure)
					: fetchParentAccountGroupByAccount(account, accountStructure);
					
					const getTreeId = (account) =>
						account?.associated_account_id != null
        				? account.associated_account_id
        				: account?.account_id;
						
						const accountGroupIndex = accountGroup.findIndex(
							(groupAccount) => getTreeId(groupAccount) === getTreeId(account)
						);
						
						if (accountGroupIndex < 0) {
							dispatch(failure('Unable to find account in current account group.'));
							
							return;
						}

						const isAssociatedAccount = account.associated_account_id != null;

						if (!isAssociatedAccount && account.is_change_in) {
							dispatch(failure('Change-in accounts cannot be moved right.'));
							return;
						}
						
						const nextAccount = accountGroup[accountGroupIndex + 1] ?? null;
						
						
						if (nextAccount == null) {
							dispatch(failure('Account cannot be moved right.'));
						return;
					}
				
					const movedAccount = {
						...account,
					parent:
						nextAccount.associated_account_id == null
						? nextAccount.account_id
						: nextAccount.associated_account_id,
					index: nextAccount.children != null ? nextAccount.children.length : 0,
					};

					const response = await axios.put('/accounts/move/right', {
					account: movedAccount,
					accountGroup: accountGroup,
					});

				if (response.status === 200) dispatch(success(response.data));
				if (response.status === 500) dispatch(failure(response.data.toString()));
			
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure(
						'Unable to move account to the right at this moment. Please try again later.',
					),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.MOVE_ACCOUNT_RIGHT_REQUEST };
	}
	function success(accountStructure) {
		return {
			type: accountConstants.MOVE_ACCOUNT_RIGHT_SUCCESS,
			accountStructure: accountStructure,
		};
	}
	function failure(error) {
		return { type: accountConstants.MOVE_ACCOUNT_RIGHT_FAILURE, error };
	}
}

function createCustomTab(name) {
	return async (dispatch) => {
		dispatch(request());

		try {
			const response = await axios.post('/custom-tab', {
				name: name,
			});

			if (response.status === 201) dispatch(success(response.data));
			if (response.status === 500) dispatch(failure(response.data.toString()));
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure(
						'Unable to update account structure at this moment. Please try again later.',
					),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.CREATE_CUSTOM_TAB_REQUEST };
	}
	function success(accountStructure) {
		return {
			type: accountConstants.CREATE_CUSTOM_TAB_SUCCESS,
			accountStructure,
		};
	}
	function failure(error) {
		return { type: accountConstants.CREATE_CUSTOM_TAB_FAILURE, error };
	}
}

function updateCustomTab(tabNameInput, currentCustomTab) {
	return async (dispatch) => {
		dispatch(request());

		try {
			let url = '/custom-tab/' + currentCustomTab.financial_group_type;

			const response = await axios.put(url, {
				name: tabNameInput,
			});

			if (response.status === 200) dispatch(success(response.data));
			if (response.status === 500) dispatch(failure(response.data.toString()));
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure(
						'Unable to update account structure at this moment. Please try again later.',
					),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.UPDATE_CUSTOM_TAB_REQUEST };
	}
	function success(accountStructure) {
		return {
			type: accountConstants.UPDATE_CUSTOM_TAB_SUCCESS,
			accountStructure,
		};
	}
	function failure(error) {
		return { type: accountConstants.UPDATE_CUSTOM_TAB_FAILURE, error };
	}
}

function moveCustomTabAccountUp(account) {
    return async (dispatch, getState) => {
        dispatch(request());

        try {
            const customTabs =
                getState().accounts.accountStructure.custom;

            const customTab = customTabs.find(
                (tab) =>
                    tab.financial_group_type ===
                    account.financial_group_type,
            );

            if (!customTab) {
                dispatch(
                    failure('Unable to find the custom tab.'),
                );
                return;
            }

            const customAccountGroup =
                customTab.custom_tab_accounts;

            if (!Array.isArray(customAccountGroup)) {
                dispatch(
                    failure(
                        'Unable to find the custom account group.',
                    ),
                );
                return;
            }

            const currentIndex =
                customAccountGroup.findIndex(
                    (customAccount) =>
                        customAccount.custom_tab_accounts_id ===
                        account.custom_tab_accounts_id,
                );

            if (currentIndex <= 0) {
                dispatch(
                    failure(
                        currentIndex < 0
                            ? 'Unable to find the custom account in its group.'
                            : 'Account cannot be moved further up.',
                    ),
                );
                return;
            }

            const response = await axios.put(
                '/custom-tab/move/up',
                {
                    moveCustomAccountUp: {
                        ...account,
                        index: currentIndex,
                    },
                    customAccountGroup,
                },
            );

            if (response.status === 200) {
                dispatch(success(response.data));
            }
        } catch (error) {
            console.error(error);

            if (error.response) {
                dispatch(
                    failure(
                        error.response.data.toString(),
                    ),
                );
            } else {
                dispatch(
                    failure(
                        'Unable to update account structure at this moment. Please try again later.',
                    ),
                );
            }
        }
    };

    function request() {
        return {
            type: accountConstants.MOVE_CUSTOM_TAB_ACCOUNT_UP_REQUEST,
        };
    }

    function success(accountStructure) {
        return {
            type: accountConstants.MOVE_CUSTOM_TAB_ACCOUNT_UP_SUCCESS,
            accountStructure,
        };
    }

    function failure(error) {
        return {
            type: accountConstants.MOVE_CUSTOM_TAB_ACCOUNT_UP_FAILURE,
            error,
        };
    }
}

function moveCustomTabAccountDown(account) {
    return async (dispatch, getState) => {
        dispatch(request());

        try {
            const customTabs =
                getState().accounts.accountStructure.custom;

            const customTab = customTabs.find(
                (tab) =>
                    tab.financial_group_type ===
                    account.financial_group_type,
            );

            if (!customTab) {
                dispatch(
                    failure('Unable to find the custom tab.'),
                );
                return;
            }

            const customAccountGroup =
                customTab.custom_tab_accounts;

            if (!Array.isArray(customAccountGroup)) {
                dispatch(
                    failure(
                        'Unable to find the custom account group.',
                    ),
                );
                return;
            }

            const currentIndex =
                customAccountGroup.findIndex(
                    (customAccount) =>
                        customAccount.custom_tab_accounts_id ===
                        account.custom_tab_accounts_id,
                );

            if (
                currentIndex < 0 ||
                currentIndex >=
                    customAccountGroup.length - 1
            ) {
                dispatch(
                    failure(
                        currentIndex < 0
                            ? 'Unable to find the custom account in its group.'
                            : 'Account cannot be moved further down.',
                    ),
                );
                return;
            }

            const response = await axios.put(
                '/custom-tab/move/down',
                {
                    moveCustomAccountDown: {
                        ...account,
                        index: currentIndex,
                    },
                    customAccountGroup,
                },
            );

            if (response.status === 200) {
                dispatch(success(response.data));
            }
        } catch (error) {
            console.error(error);

            if (error.response) {
                dispatch(
                    failure(
                        error.response.data.toString(),
                    ),
                );
            } else {
                dispatch(
                    failure(
                        'Unable to update account structure at this moment. Please try again later.',
                    ),
                );
            }
        }
    };

    function request() {
        return {
            type: accountConstants.MOVE_CUSTOM_TAB_ACCOUNT_DOWN_REQUEST,
        };
    }

    function success(accountStructure) {
        return {
            type: accountConstants.MOVE_CUSTOM_TAB_ACCOUNT_DOWN_SUCCESS,
            accountStructure,
        };
    }

    function failure(error) {
        return {
            type: accountConstants.MOVE_CUSTOM_TAB_ACCOUNT_DOWN_FAILURE,
            error,
        };
    }
}

function reorderCustomTabs(order) {
		return async (dispatch, getState) => {
			const previousCustom =
				getState().accounts.accountStructure?.custom;

			if (!Array.isArray(previousCustom)) return;

			/*
			* Reorder locally first so the tab bar settles immediately;
			* the server response replaces the whole structure anyway.
			*/
			const byType = new Map(
				previousCustom.map((tab) => [tab.financial_group_type, tab]),
			);

			const optimisticCustom = order
				.map((type) => byType.get(type))
				.filter(Boolean)
				.map((tab, index) => ({ ...tab, tab_index: index }));

			if (optimisticCustom.length !== previousCustom.length) return;

			dispatch(request(optimisticCustom));

			try {
				const response = await axios.put('/custom-tab/order', { order });

				if (response.status === 200 || response.status === 201) {
					dispatch(success(response.data));
					return response.data;
				}

				dispatch(failure('Failed reordering custom tabs', previousCustom));
			} catch (error) {
				console.error(error);

				dispatch(
					failure(
						error.response?.data?.error ??
							error.response?.data?.message ??
							'Unable to reorder custom tabs at this moment. Please try again later.',
						previousCustom,
					),
				);
			}
		};

		function request(custom) {
			return {
				type: accountConstants.REORDER_CUSTOM_TABS_REQUEST,
				custom,
			};
		}
		function success(accountStructure) {
			return {
				type: accountConstants.REORDER_CUSTOM_TABS_SUCCESS,
				accountStructure,
			};
		}
		function failure(error, custom) {
			return {
				type: accountConstants.REORDER_CUSTOM_TABS_FAILURE,
				error,
				custom,
			};
		}
	}

function deleteCustomTab(financialGroupType) {
	return async (dispatch) => {
		dispatch(request());

		try {
			const url = '/custom-tab/' + financialGroupType;
			const response = await axios.delete(url);

			if (response.status === 200) dispatch(success(response.data));
			if (response.status === 500) dispatch(failure(response.data.toString()));
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure(
						'Unable to update account structure at this moment. Please try again later.',
					),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.DELETE_CUSTOM_TAB_REQUEST };
	}
	function success(accountStructure) {
		return {
			type: accountConstants.DELETE_CUSTOM_TAB_SUCCESS,
			accountStructure,
		};
	}
	function failure(error) {
		return { type: accountConstants.DELETE_CUSTOM_TAB_FAILURE, error };
	}
}

/**
 * Pins one or more accounts to a custom tab in one request.
 * pins: [{ account_id, childAccountIds }]
 */
function createCustomTabAccount(
	groupId,
	financialGroupType,
	accountStructure,
	{ pins = [], autoAddChildren = true } = {},
) {
	return async (dispatch) => {
		dispatch(request());

		try {
			const customTab = accountStructure?.custom?.find(
				(tab) => tab.financial_group_type === financialGroupType,
			);

			const customIndex = customTab?.custom_tab_accounts?.length ?? 0;

			const response = await axios.post('/custom-tab/account/', {
				pins,
				customIndex,
				group_id: groupId,
				financial_group_type: financialGroupType,
				autoAddChildren,
			});

			if (response.status === 201) dispatch(success(response.data));
			if (response.status === 500) dispatch(failure(response.data.toString()));
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure(
						'Unable to create custom tab account at this moment. Please try again later.',
					),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.CREATE_CUSTOM_TAB_ACCOUNT_REQUEST };
	}
	function success(accountStructure) {
		return {
			type: accountConstants.CREATE_CUSTOM_TAB_ACCOUNT_SUCCESS,
			accountStructure,
		};
	}
	function failure(error) {
		return {
			type: accountConstants.CREATE_CUSTOM_TAB_ACCOUNT_FAILURE,
			error,
		};
	}
}

function updateCustomTabAccount(account) {
	return async (dispatch) => {
		dispatch(request());

		try {
			const url = '/custom-tab/account/' + account.account_id;
			const response = await axios.put(url, {
				account: account,
			});

			if (response.status === 200) dispatch(success(response.data));
			if (response.status === 500) dispatch(failure(response.data.toString()));
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure(
						'Unable to update custom tab account at this moment. Please try again later.',
					),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.UPDATE_CUSTOM_TAB_ACCOUNT_REQUEST };
	}
	function success(accountStructure) {
		return {
			type: accountConstants.UPDATE_CUSTOM_TAB_ACCOUNT_SUCCESS,
			accountStructure,
		};
	}
	function failure(error) {
		return {
			type: accountConstants.UPDATE_CUSTOM_TAB_ACCOUNT_FAILURE,
			error,
		};
	}
}

function updateCustomTabAccountSettings(customAccount, { autoAddChildren }) {
	return async (dispatch) => {
		dispatch({ type: accountConstants.UPDATE_CUSTOM_TAB_ACCOUNT_REQUEST });

		try {
			const url = '/custom-tab/account/' + customAccount.custom_tab_accounts_id + '/settings';
			const response = await axios.put(url, { autoAddChildren });

			dispatch({
				type: accountConstants.UPDATE_CUSTOM_TAB_ACCOUNT_SUCCESS,
				accountStructure: response.data,
			});
		} catch (error) {
			console.error(error);
			dispatch({
				type: accountConstants.UPDATE_CUSTOM_TAB_ACCOUNT_FAILURE,
				error: error.response
					? String(error.response.data)
					: 'Unable to update custom tab account at this moment. Please try again later.',
			});
		}
	};
}

function deleteCustomTabAccount(account) {
	return async (dispatch) => {
		dispatch(request());

		try {
			const url = '/custom-tab/account/' + account.custom_tab_accounts_id;
			const response = await axios.delete(url, {
				data: {
					customAccount: account,
				},
			});

			if (response.status === 200) dispatch(success(response.data));
			if (response.status === 500) dispatch(failure(response.data.toString()));
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure(
						'Unable to delete custom account at this moment. Please try again later.',
					),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.DELETE_CUSTOM_TAB_ACCOUNT_REQUEST };
	}
	function success(accountStructure) {
		return {
			type: accountConstants.DELETE_CUSTOM_TAB_ACCOUNT_SUCCESS,
			accountStructure,
		};
	}
	function failure(error) {
		return {
			type: accountConstants.DELETE_CUSTOM_TAB_ACCOUNT_FAILURE,
			error,
		};
	}
}

function createChangeInAccount(cashEffect, changeInAccount) {
	return async (dispatch, getState) => {
		dispatch(request());

		try {
			const accountStructure = getState().accounts.accountStructure;
			const scenarios = getState().scenarios.scenarios;
			const periods = getState().periods.periods;
			const scenarioIds = Array.from(getState().scenarios.scenarios.keys());
			const associatedIndexes = {
				indexPL: accountStructure.profitloss.length,
				indexCF: accountStructure.cashflow.length,
			};

			const newScenarios = {};
			for (let [key, value] of scenarios) {
				newScenarios[key] = {
					compiled: value.compiled,
					scenario_id: value.scenario_id,
					originalValues: Object.fromEntries(value.originalValues),
					values: Object.fromEntries(value.values),
					roll_up: Object.fromEntries(value.roll_up),
				};
			}


			const response = await axios.post('/changeIn', {
				scenarios: newScenarios,
				periods: periods,
				changeInAccount: changeInAccount,
				cashEffect: cashEffect,
				associatedIndexes: associatedIndexes,
			});

			if (response.status === 201) {
				dispatch(success(response.data.accountStructure));
				if (
					response.data.newScenarios != null &&
					Object.keys(response.data.newScenarios).length > 0
				) {
					dispatch(scenarioActions.createChangeInValues(response));
				}
			}
			if (response.status === 500) dispatch(failure(response.data.toString()));
		} catch (error) {
			console.error(error);
			if (error.response) {
				dispatch(failure(error.response.data.toString()));
			} else {
				dispatch(
					failure(
						'Unable to update account structure at this moment. Please try again later.',
					),
				);
			}
		}
	};

	function request() {
		return { type: accountConstants.CREATE_CHANGE_IN_ACCOUNT_REQUEST };
	}
	function success(accountStructure) {
		return {
			type: accountConstants.CREATE_CHANGE_IN_ACCOUNT_SUCCESS,
			accountStructure,
		};
	}
	function failure(error) {
		return {
			type: accountConstants.CREATE_CHANGE_IN_ACCOUNT_FAILURE,
			error,
		};
	}
}

function createSeparator(payload) {
    return async (dispatch) => {
        dispatch(request());

        try {
            const response = await axios.post(
                '/account-separator',
                payload,
            );

            if (response.status === 201) {
                dispatch(success(response.data));
                return response.data;
            }

            dispatch(
                failure(
                    response.data?.toString?.() ??
                        'Failed creating separator',
                ),
            );
        } catch (error) {
            console.error(error);

            if (error.response) {
                dispatch(
                    failure(
                        typeof error.response.data ===
                        'string'
                            ? error.response.data
                            : error.response.data?.error ??
                                  'Failed creating separator',
                    ),
                );
            } else {
                dispatch(
                    failure(
                        'Unable to create separator at this moment. Please try again later.',
                    ),
                );
            }
        }
    };

    function request() {
        return {
            type:
                accountConstants
                    .CREATE_SEPARATOR_REQUEST,
        };
    }

    function success(accountStructure) {
        return {
            type:
                accountConstants
                    .CREATE_SEPARATOR_SUCCESS,
            accountStructure,
        };
    }

    function failure(error) {
        return {
            type:
                accountConstants
                    .CREATE_SEPARATOR_FAILURE,
            error,
        };
    }
}

function updateSeparator(separatorId, updates) {
    return async (dispatch) => {
        dispatch(request());

        try {
            const response = await axios.put(
                `/account-separator/${separatorId}`,
                updates,
            );

            if (response.status === 200) {
                dispatch(success(response.data));
                return response.data;
            }

            dispatch(
                failure(
                    response.data?.toString?.() ??
                        'Failed updating separator',
                ),
            );

            return null;
        } catch (error) {
            console.error(error);

            if (error.response) {
                dispatch(
                    failure(
                        typeof error.response.data ===
                        'string'
                            ? error.response.data
                            : error.response.data?.error ??
                                  'Failed updating separator',
                    ),
                );
            } else {
                dispatch(
                    failure(
                        'Unable to update separator at this moment. Please try again later.',
                    ),
                );
            }

            return null;
        }
    };

    function request() {
        return {
            type:
                accountConstants
                    .UPDATE_SEPARATOR_REQUEST,
        };
    }

    function success(accountStructure) {
        return {
            type:
                accountConstants
                    .UPDATE_SEPARATOR_SUCCESS,
            accountStructure,
        };
    }

    function failure(error) {
        return {
            type:
                accountConstants
                    .UPDATE_SEPARATOR_FAILURE,
            error,
        };
    }
}

function deleteSeparator(separatorId) {
    return async (dispatch) => {
        dispatch(request());

        try {
            const response = await axios.delete(
                `/account-separator/${separatorId}`,
            );

            if (response.status === 200) {
                dispatch(success(response.data));
                return response.data;
            }

            dispatch(
                failure('Failed deleting separator'),
            );
        } catch (error) {
            console.error(error);

            if (error.response) {
                dispatch(
                    failure(
                        typeof error.response.data === 'string'
                            ? error.response.data
                            : error.response.data?.error ??
                                  'Failed deleting separator',
                    ),
                );
            } else {
                dispatch(
                    failure(
                        'Unable to delete separator at this moment. Please try again later.',
                    ),
                );
            }
        }
    };

    function request() {
        return {
            type: accountConstants.DELETE_SEPARATOR_REQUEST,
        };
    }

    function success(accountStructure) {
        return {
            type: accountConstants.DELETE_SEPARATOR_SUCCESS,
            accountStructure,
        };
    }

    function failure(error) {
        return {
            type: accountConstants.DELETE_SEPARATOR_FAILURE,
            error,
        };
    }
}

function moveSeparator(separatorId, direction) {
    return async (dispatch) => {
        dispatch(request());

        try {
            const response = await axios.patch(
                `/account-separator/${separatorId}/move`,
                {
                    direction,
                },
            );

            if (response.status === 200) {
                dispatch(success(response.data));
                return response.data;
            }

            dispatch(
                failure('Failed moving separator'),
            );
        } catch (error) {
            console.error(error);

            dispatch(
                failure(
                    error.response?.data?.error ??
                        error.response?.data?.message ??
                        'Unable to move separator.',
                ),
            );
        }
    };

    function request() {
        return {
            type: accountConstants.MOVE_SEPARATOR_REQUEST,
        };
    }

    function success(accountStructure) {
        return {
            type: accountConstants.MOVE_SEPARATOR_SUCCESS,
            accountStructure,
        };
    }

    function failure(error) {
        return {
            type: accountConstants.MOVE_SEPARATOR_FAILURE,
            error,
        };
    }
}